/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import { LocationModel } from "@insite/client-framework/Common/Hooks/useLocationFilterSearch";
import { Dictionary, SafeDictionary } from "@insite/client-framework/Common/Types";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

export type MarketoModalForms = Readonly<{
    // Loading indicator for marketo forms
    marketoLoading: boolean;
    // Form Diemensions
    height?: number;
    width?: number;
    // Confirmation Message as filled in the widget
    successMessage?: string;
    // Show & hide confirmation message for normal & modal forms
    showFormConfirmationMessage: boolean;
    // This object will save the marketo forms that were created inside modals
    modalFormsData: SafeDictionary<{ formId: string; uuid: string }>;
    // This object will help to identify which form is submitted to show & hide confirmation message
    activeForm: {
        formUuid: string;
        formId: string;
    };
    customCss?: string;
    extraLocationData?: {
        comingFromDealerPage: boolean;
        inShell?: boolean;
        location: LocationModel & { properties: Dictionary<string> };
    };
    totalLoadedForms: number;
    totalForms: number;
}>;

export const InitialMarketoModalFormsState: MarketoModalForms = {
    marketoLoading: true,
    successMessage: undefined,
    showFormConfirmationMessage: false,
    modalFormsData: {},
    activeForm: {
        formUuid: "",
        formId: "",
    },
    customCss: "",
    extraLocationData: undefined,
    totalLoadedForms: 0,
    totalForms: 0,
};

const reducer = {
    "MarketoForm/StartLoading": (draft: Draft<MarketoModalForms>) => {
        draft.marketoLoading = true;
    },
    "MarketoForm/StopLoading": (draft: Draft<MarketoModalForms>) => {
        draft.marketoLoading = false;
    },
    "MarketoForm/DisplayFormConfirmationMessage": (
        draft: Draft<MarketoModalForms>,
        action: {
            uuid: string;
            formId: string;
        },
    ) => {
        draft.showFormConfirmationMessage = true;
        draft.activeForm.formUuid = action.uuid;
        draft.activeForm.formId = action.formId;
    },

    "MarketoForm/SetModalForm": (
        draft: Draft<MarketoModalForms>,
        action: {
            uuid: string;
            formId: string;
        },
    ) => {
        if (!draft.modalFormsData[action.uuid]) {
            draft.modalFormsData[action.uuid] = {
                uuid: action.uuid,
                formId: action.formId,
            };
        }
    },

    "MarketoForm/SetFormLoaded": (draft: Draft<MarketoModalForms>) => {
        draft.totalLoadedForms += 1;
    },

    "MarketoForm/SetTotalForms": (
        draft: Draft<MarketoModalForms>,
        action: {
            numberOfForms: number;
        },
    ) => {
        draft.totalForms = action.numberOfForms;
    },
};

const xMarketoModalForms = createTypedReducerWithImmer(InitialMarketoModalFormsState, reducer);

(reducers as any).xMarketoModalForms = xMarketoModalForms;

export type xMarketoModalFormsState = {
    xMarketoModalForms: MarketoModalForms;
};

type xMarketoModalFormsReducers = {
    [key: string]: typeof xMarketoModalForms;
};

export type xMarketoModalFormsActions =
    | Parameters<xMarketoModalFormsReducers[keyof xMarketoModalFormsReducers]>[1]
    | AnyAction;
